import {
    Card,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    Toggle,
} from '../../components';
import { CheckCircle, ChevronDown } from '../../icons';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import InputSelect from '../../components/InputSelect';

type IntroModuleExercise3NotificationSettingsProps = {
    initialEnabled: boolean;
    initialDayOfWeek: number | null;
    initialTime: string;
    onSave: (settings: {
        dayOfWeek: number | null;
        time: string;
        enabled: boolean;
    }) => void;
};

const weekdays = [
    { value: 2, label: 'Mandag' },
    { value: 3, label: 'Tirsdag' },
    { value: 4, label: 'Onsdag' },
    { value: 5, label: 'Torsdag' },
    { value: 6, label: 'Fredag' },
    { value: 7, label: 'Lørdag' },
    { value: 1, label: 'Søndag' },
];

export function IntroModuleExercise3NotificationSettings({
    initialEnabled,
    initialDayOfWeek,
    initialTime,
    onSave,
}: IntroModuleExercise3NotificationSettingsProps) {
    const [enabled, setEnabled] = useState(initialEnabled);
    const [changesSaved, setChangesSaved] = useState(false);

    const now = new Date();
    const currentJsDay = now.getDay(); // 0 = Sunday, 1 = Monday, ...
    const currentDayOfWeek = currentJsDay === 0 ? 1 : currentJsDay + 1;
    const currentWeekday =
        weekdays.find((day) => day.value === currentDayOfWeek)?.label ??
        'Mandag';

    const currentTime = now.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
    });

    const [dayOfWeek, setDayOfWeek] = useState(
        initialDayOfWeek ?? currentDayOfWeek,
    );

    const [time, setTime] = useState(initialTime || currentTime);

    useEffect(() => {
        setEnabled(initialEnabled);
        setDayOfWeek(initialDayOfWeek ?? currentDayOfWeek);
        setTime(initialTime || currentTime);
    }, [
        initialEnabled,
        initialDayOfWeek,
        initialTime,
        currentDayOfWeek,
        currentTime,
    ]);

    const timePlaceholder = enabled ? currentTime : 'Vælg tidspunkt';

    const weekdayPlaceholder = enabled ? currentWeekday : 'Vælg ugedag';

    return (
        <Card>
            <LabelDisplay1 className="mb-10">
                Vil du mindes om at tjekke ind med dig selv?
            </LabelDisplay1>

            <LabelBody>
                Vælg en ugedag og et tidspunkt, hvor du gerne vil mindes om at
                tjekke ind med dig selv.
            </LabelBody>

            <LabelBody className="mb-5 mt-15">Send notifikation</LabelBody>
            <div className="mb-30 flex flex-row items-center">
                <Toggle
                    id="introExercise3NotificationToggle"
                    value={enabled}
                    onChangeValue={(newEnabled) => {
                        setEnabled(newEnabled);

                        if (!newEnabled) {
                            onSave({
                                dayOfWeek,
                                time,
                                enabled: false,
                            });
                            setChangesSaved(true);
                            return;
                        }

                        setChangesSaved(false);
                    }}
                />
                <label
                    htmlFor="introExercise3NotificationToggle"
                    className="ml-10 cursor-pointer self-center text-body text-dark-red"
                >
                    {enabled ? 'Til' : 'Fra'}
                </label>
            </div>

            <InputSelect
                label="Ugedag"
                value={dayOfWeek}
                onChange={(value) => {
                    setDayOfWeek(value);
                    setChangesSaved(false);
                }}
                options={weekdays}
                disabled={!enabled}
                placeholder={weekdayPlaceholder}
            />

            <InputSelect
                label="Tidspunkt"
                value={time}
                onChange={(value) => {
                    setTime(value ?? '');
                    setChangesSaved(false);
                }}
                disabled={!enabled}
                placeholder={timePlaceholder}
                type="time"
            />

            <GhostButton
                disabled={!enabled}
                onClick={() => {
                    onSave({ dayOfWeek, time, enabled });
                    setChangesSaved(true);
                }}
            >
                Gem ændringer
            </GhostButton>

            {changesSaved && (
                <div className="mt-30 flex flex-row">
                    <CheckCircle className="mr-10 self-center text-success" />
                    <LabelBodyHeadline>Ændringerne er gemt</LabelBodyHeadline>
                </div>
            )}
        </Card>
    );
}
